import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { temperatureContext } from '../../../lib/context';
import './DataPlot.css';
import FailDurationCard from './FailDurationCard';
import Progress from './Progress';
import RecentUploadsTable from './RecentUploadsTable';
import TemperatureCard from './TemperatureCard';
import UploadsChart from './UploadsChart';
import { CARD_BASE_STYLE, CARD_DIMENSIONS, COLORS, TITLE_STYLES } from './dataPlotConstants';
import { formatFailTime, normalizeUploadRecords, processTimeData, sumCounts } from './dataPlotUtils';

function DataPlot({
    allCountList,
    errCountList,
    overviewRecords,
    normal,
    abnormal,
    failAverageTime,
    errRate,
    timeKeys,
    isLoading,
    isMobile,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Overview' });
    const tempUnit = useContext(temperatureContext);
    const timeOffset = dayjs().utcOffset();

    const uploadData = normalizeUploadRecords(overviewRecords, timeOffset);
    const { allList, errList } = processTimeData(timeKeys, allCountList, errCountList);
    const interval = allList.length > 7 ? Math.round(allCountList.length / 7) : 1;
    const keys = ['time', 'region', 'company', 'status'];

    const totalUploads = sumCounts(allCountList);
    const totalErrors = sumCounts(errCountList);

    const rawErrRate = Number(errRate);
    const errRatePercentage = Number.isFinite(rawErrRate)
        ? Math.max(0, Math.min(100, Math.round(rawErrRate > 1 ? rawErrRate : rawErrRate * 100)))
        : 0;

    const errRateSubtitle = totalUploads || totalErrors ? `${totalErrors} / ${totalUploads || '-'}` : undefined;

    const buildCardStyle = (size: any, overrides: any = {}) => ({
        ...CARD_BASE_STYLE,
        ...(isMobile ? { margin: '10px 0', padding: '16px 12px' } : {}),
        ...(isMobile
            ? CARD_DIMENSIONS[size as keyof typeof CARD_DIMENSIONS].mobile
            : CARD_DIMENSIONS[size as keyof typeof CARD_DIMENSIONS].desktop),
        ...overrides,
    });

    const baseTitleStyle = isMobile ? { ...TITLE_STYLES.base, ...TITLE_STYLES.mobile } : TITLE_STYLES.base;

    const titleWithMargin = {
        ...baseTitleStyle,
        ...(isMobile ? {} : TITLE_STYLES.withMargin),
        ...(isMobile ? { margin: '10px 0 6px' } : {}),
    };

    const titleWithTopMargin = {
        ...baseTitleStyle,
        ...(isMobile ? {} : TITLE_STYLES.withTopMargin),
        ...(isMobile ? { margin: '14px 0 8px' } : {}),
    };

    const uploadsChart = (
        <UploadsChart
            allList={allList}
            errList={errList}
            interval={interval}
            isMobile={isMobile}
            isLoading={isLoading}
            titleWithMargin={titleWithMargin}
            buildCardStyle={buildCardStyle}
        />
    );

    const { failDay, failHour, failMinute } = formatFailTime(failAverageTime);

    const failDurationCard = (
        <FailDurationCard
            failDay={failDay}
            failHour={failHour}
            failMinute={failMinute}
            isMobile={isMobile}
            isLoading={isLoading}
            baseTitleStyle={baseTitleStyle}
            buildCardStyle={buildCardStyle}
        />
    );

    const newPassTemperature = (
        <TemperatureCard
            title={t('passAverageTemp')}
            label={t('pass')}
            accent={COLORS.primary}
            accentLight={COLORS.primaryLight}
            accentBg={COLORS.primaryBg}
            values={normal}
            type='pass'
            tempUnit={tempUnit}
            isMobile={isMobile}
            isLoading={isLoading}
            buildCardStyle={buildCardStyle}
            baseTitleStyle={baseTitleStyle}
        />
    );

    const newFailTemperature = (
        <TemperatureCard
            title={t('failAverageTemp')}
            label={t('fail')}
            accent={COLORS.error}
            accentLight={COLORS.errorLight}
            accentBg={COLORS.errorBg}
            values={abnormal}
            type='fail'
            tempUnit={tempUnit}
            isMobile={isMobile}
            isLoading={isLoading}
            buildCardStyle={buildCardStyle}
            baseTitleStyle={baseTitleStyle}
        />
    );

    const tableRows = isMobile ? uploadData : uploadData.slice(0, 7);
    const columnWidths = isMobile
        ? {
              time: '25%',
              region: '20%',
              company: '35%',
              status: '20%',
          }
        : {};

    const information = (
        <RecentUploadsTable
            tableRows={tableRows}
            columnWidths={columnWidths}
            keys={keys}
            isMobile={isMobile}
            isLoading={isLoading}
            titleWithTopMargin={titleWithTopMargin}
            buildCardStyle={buildCardStyle}
        />
    );

    const failRateCard = isMobile ? (
        <div className='mobile-card'>
            <p className='mobile-card__title'>{t('errorRate')}</p>
            <Progress errRate={errRatePercentage} subtitle={errRateSubtitle} variant='mobile' isLoading={isLoading} />
        </div>
    ) : (
        <div className='card' style={buildCardStyle('small', { alignItems: 'center' }) as any}>
            <div style={baseTitleStyle as any}>{t('errorRate')}</div>
            <Progress errRate={errRatePercentage} isLoading={isLoading} />
        </div>
    );

    const metricsSection = isMobile ? (
        <div className='overview-mobile-grid'>
            {failRateCard}
            {failDurationCard}
            {newFailTemperature}
            {newPassTemperature}
        </div>
    ) : (
        <div className='overview-metrics'>
            {failRateCard}
            {failDurationCard}
            {newPassTemperature}
            {newFailTemperature}
        </div>
    );

    const bottomSection = isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
            {information}
            {uploadsChart}
        </div>
    ) : (
        <div className='overview-bottom'>
            {information}
            {uploadsChart}
        </div>
    );

    return (
        <div
            style={{
                display: 'flex',
                flex: 'auto',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
                gap: isMobile ? '16px' : '24px',
            }}
        >
            {metricsSection}
            {bottomSection}
        </div>
    );
}

export default DataPlot;
